import SidePanel from '~/components/SidePanel';
import { useGiftCertificateDetailsQuery } from '~/lib/Queries';
import MoneyInput from '~/components/MoneyInput';
import Loading from '~/components/Loading';
import { useEffect, useState } from 'react';
import useBusinessPostApi from '~/lib/useBusinessPostApi';
import * as Toast from '~/lib/Toast';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import { formatDate, formatDateTime } from '~/lib/DateTime';
import useDelete from '~/lib/useDelete';
import SendOnInput from '~/components/giftcertificates/SendOnInput';
import TextInput from '~/components/TextInput';
import EmailInput from '~/components/EmailInput';
import DateInput from '~/components/DateInput';
import ExpiresDateInput from '~/components/coupons/ExpiresDateInput';

const GiftCertificatePanel = ({ id, isOpen, onClose, refetch }) => {
    const { data, isLoading } = useGiftCertificateDetailsQuery(id);
    const { save, isSaving } = useBusinessPostApi(`/gift-certificates/${id}`);
    const { saveDelete, isDeleting } = useDelete(
        `/gift-certificates/${id}/delete`,
    );

    const [localData, setLocalData] = useState({});

    const giftCertificate = data?.giftCertificate || {};
    const usages = giftCertificate.usages || [];

    useEffect(() => {
        if (localData?.id !== giftCertificate?.id && giftCertificate?.id) {
            setLocalData(giftCertificate);
        }
    }, [giftCertificate]);

    function doSave() {
        save(localData).then(result => {
            Toast.success('Successfully saved gift certificate');
            onClose();

            if (typeof refetch === 'function') {
                refetch();
            }
        });
    }

    function doDelete() {
        saveDelete().then(() => {
            onClose();
            if (typeof refetch === 'function') {
                refetch();
            }
        });
    }

    return (
        <SidePanel
            isOpen={isOpen}
            onClose={onClose}
            onCancel={onClose}
            onSave={doSave}
            saveLabel={isSaving ? 'Saving...' : 'Save'}
            showSave={true}
        >
            <h3>Gift Certificate</h3>
            {isLoading && <Loading />}
            {!isLoading && (
                <div>
                    <div className="my-4">
                        <div className="panel-label">Code</div>
                        <div className="panel p-4 flex items-center justify-between">
                            {giftCertificate.code?.code}
                            {!!giftCertificate.view_link && (
                                <a
                                    href={giftCertificate.view_link}
                                    target="_blank"
                                    className="btn-light-pill-sm uppercase"
                                >
                                    View
                                </a>
                            )}
                        </div>
                    </div>

                    <div className="input-label">Total Value</div>
                    <div className="panel pl-1 py-2 pr-4 flex items-center justify-between">
                        <MoneyInput
                            inputClassName="border-none"
                            value={localData.value}
                            onChange={value =>
                                setLocalData({ ...localData, value: value })
                            }
                        />
                        <div className="text-gray-500">
                            {formatCurrency(giftCertificate.remaining_value)}{' '}
                            Remaining
                        </div>
                    </div>

                    {!giftCertificate.sent_at && (
                        <SendOnInput
                            className="mt-4"
                            value={localData?.send_at}
                            onChange={value =>
                                setLocalData(old => ({
                                    ...old,
                                    send_at: value,
                                }))
                            }
                        />
                    )}

                    {(localData.send_at || giftCertificate.sent_at) && (
                        <div className="mt-4">
                            <div className="panel-label">Delivery</div>
                            <div className="panel mt-2 p-4">
                                {!!giftCertificate.sent_at && (
                                    <>
                                        This was delivered{' '}
                                        {formatDateTime(
                                            giftCertificate.sent_at,
                                        )}
                                    </>
                                )}
                                {!giftCertificate.sent_at &&
                                    !!localData.send_at && (
                                        <>
                                            This will be sent to{' '}
                                            {localData.recipient?.email || (
                                                <span className="text-red-700">
                                                    no email provided
                                                </span>
                                            )}{' '}
                                            on {formatDate(localData.send_at)}
                                        </>
                                    )}
                            </div>
                        </div>
                    )}

                    <TextInput
                        label="Recipient Name"
                        className="mt-4"
                        value={localData?.recipient?.name || ''}
                        onChange={value =>
                            setLocalData(old => ({
                                ...old,
                                recipient: {
                                    ...(old.recipient || {}),
                                    name: value,
                                },
                            }))
                        }
                    />

                    <EmailInput
                        label="Recipient Email"
                        className="mt-4"
                        value={localData?.recipient?.email || ''}
                        onChange={value =>
                            setLocalData(old => ({
                                ...old,
                                recipient: {
                                    ...(old.recipient || {}),
                                    email: value,
                                },
                            }))
                        }
                    />

                    <div className="mt-4">
                        <div className="input-label">Expiration</div>
                        <div className="panel">
                            <ExpiresDateInput
                                value={localData?.expires_at}
                                onChange={value =>
                                    setLocalData(old => ({
                                        ...old,
                                        expires_at: value,
                                    }))
                                }
                                defaultExpirationDays={365}
                            />
                        </div>
                    </div>

                    <div className="panel-label mt-4">Activity</div>
                    <div className="mt-2 panel pl-4 py-2 divide-y">
                        {!usages.length && 'This has not been redeemed'}
                        {usages.map(u => (
                            <div className="py-2 pr-4 flex items-center justify-between">
                                <div>
                                    {u.paymentable?.name ||
                                        u.paymentable?.form?.name ||
                                        'Payment'}
                                    <div className="text-gray-500 text-sm">
                                        {formatDateTime(u.created_at)}
                                    </div>
                                </div>
                                <div className="font-medium">
                                    {formatCurrency(u.amount)}
                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        className="btn-pink-pill mt-6"
                        onClick={doDelete}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            )}
        </SidePanel>
    );
};

export default GiftCertificatePanel;
